import dayjs from 'dayjs';
import { CalendarIcon, CheckIcon, XIcon } from 'lucide-react';
import {
  type ComponentProps,
  type FC,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { DateRange as CalendarDateRange } from 'react-day-picker';

import { cn } from '../../lib/cn';
import { Button, buttonVariants } from '../button';
import { Calendar } from '../../primitive/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../primitive/popover';
import { DateInput } from '../date-input';

import {
  clampDateRangePickerValueToBounds,
  clampDayjsToDateRangeBounds,
  getAllowedDateRangePickerPresetNames,
} from '../form/date-range-picker.utils';
import {
  DEFAULT_PRESET_NAMES,
  PRESETS,
  type PresetName,
  getPresetDateRange,
} from './get-preset-date-range';
import type { DateRange } from './types';

const DATE_FORMAT = {
  base: 'YYYY-MM-DD',
  readable: 'MMM DD, YYYY',
} as const;

export type DateRangePickerValue =
  | {
      type: 'dateRange';
      dateRange: DateRange;
    }
  | {
      type: 'preset';
      presetName: PresetName;
    };

type DateRangePickerCommonProps = {
  value?: DateRangePickerValue;
  onChange?: (value: DateRangePickerValue) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  minDate?: DateRange['from'];
  maxDate?: DateRange['to'];
  endDateAriaLabel?: string;
  startDateAriaLabel?: string;
  preservePresetSelectionWithinBounds?: boolean;
  presetNames?: readonly PresetName[];
  resolvePresetDateRange?: (presetName: PresetName) => DateRange;
  showPresets?: boolean;
  align?: ComponentProps<typeof PopoverContent>['align'];
  ariaLabel?: string;
};

type DateRangePickerBuiltInProps = DateRangePickerCommonProps & {
  type: 'compact' | 'filter';
  children?: never;
  onReset?: never;
};

type DateRangePickerResettableProps = DateRangePickerCommonProps & {
  type: 'filter-resettable';
  children?: never;
  onReset: () => void;
};

type DateRangePickerCustomProps = DateRangePickerCommonProps & {
  type: 'custom';
  children: ReactElement;
  onReset?: never;
};

export type DateRangePickerProps =
  DateRangePickerBuiltInProps | DateRangePickerResettableProps | DateRangePickerCustomProps;

type DateRangePickerDraft = {
  from: DateRange['from'];
  to?: DateRange['to'];
};

type DateRangePickerDraftValue =
  | {
      type: 'dateRange';
      dateRange: DateRangePickerDraft;
    }
  | {
      type: 'preset';
      presetName: PresetName;
    };

const createDefaultRange = (): DateRangePickerValue => {
  const today = dayjs().startOf('day');
  const thirtyDaysAgo = today.subtract(30, 'day');

  return { type: 'dateRange', dateRange: { from: thirtyDaysAgo, to: today } };
};

function getDisplayText(value: DateRangePickerValue | undefined, placeholder: string) {
  if (!value) {
    return placeholder;
  }

  if (value.type === 'preset') {
    const preset = PRESETS.find((item) => item.name === value.presetName);
    return preset?.label ?? 'Custom';
  }

  return `${value.dateRange.from.format(DATE_FORMAT.readable)} - ${value.dateRange.to.format(DATE_FORMAT.readable)}`;
}

function getComputedDateRange(
  value: DateRangePickerValue,
  resolvePresetDateRange: (presetName: PresetName) => DateRange,
): DateRange {
  if (value.type === 'preset') {
    return resolvePresetDateRange(value.presetName);
  }

  return value.dateRange;
}

type DateRangePickerPanelProps = {
  draftDateRange: DateRangePickerDraft;
  isSmallScreen: boolean;
  maxDate?: DateRange['to'];
  minDate?: DateRange['from'];
  startDateAriaLabel?: string;
  endDateAriaLabel?: string;
  onCalendarSelect: (value: CalendarDateRange | undefined, triggerDate: Date) => void;
  onCancel: () => void;
  onEndDateChange: (date: DateRange['to']) => DateRange['to'];
  onPresetSelect: (preset: PresetName) => void;
  onStartDateChange: (date: DateRange['from']) => DateRange['from'];
  onUpdate: () => void;
  presetNames: PresetName[];
  selectedPresetName?: PresetName;
  showPresets: boolean;
};

function DateRangePickerPanel({
  draftDateRange,
  isSmallScreen,
  maxDate,
  minDate,
  startDateAriaLabel,
  endDateAriaLabel,
  onCalendarSelect,
  onCancel,
  onEndDateChange,
  onPresetSelect,
  onStartDateChange,
  onUpdate,
  presetNames,
  selectedPresetName,
  showPresets,
}: DateRangePickerPanelProps) {
  const [calendarMonth, setCalendarMonth] = useState(() => draftDateRange.from.toDate());

  const handleStartInputChange = (date: DateRange['from']) => {
    setCalendarMonth(onStartDateChange(date).toDate());
  };

  const handleEndInputChange = (date: DateRange['to']) => {
    setCalendarMonth(onEndDateChange(date).toDate());
  };

  return (
    <div className="flex flex-col">
      <div className="border-b px-4 py-4">
        <div className="flex flex-row items-start justify-between gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <DateInput
                aria-label={startDateAriaLabel}
                value={draftDateRange.from}
                onChange={handleStartInputChange}
              />
              <span className="text-muted-foreground py-1">-</span>
              <DateInput
                aria-label={endDateAriaLabel}
                value={draftDateRange.to}
                onChange={handleEndInputChange}
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={draftDateRange.to == null} onClick={onUpdate}>
              Update
            </Button>
          </div>
        </div>
      </div>
      <div className="flex gap-4 p-4">
        <Calendar
          fixedWeeks
          mode="range"
          onSelect={onCalendarSelect}
          selected={{
            from: draftDateRange.from.toDate(),
            to: draftDateRange.to?.toDate(),
          }}
          disabled={(date) => {
            if (minDate && date < minDate.toDate()) {
              return true;
            }

            if (maxDate && date > maxDate.toDate()) {
              return true;
            }

            return false;
          }}
          numberOfMonths={isSmallScreen ? 1 : 2}
          month={calendarMonth}
          onMonthChange={setCalendarMonth}
        />
        {showPresets ? (
          <div className="flex min-w-40 flex-col gap-1">
            {presetNames.map((presetName) => (
              <PresetButton
                key={presetName}
                onClick={() => onPresetSelect(presetName)}
                label={PRESETS.find(({ name }) => name === presetName)?.label ?? presetName}
                isSelected={selectedPresetName === presetName}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const DateRangePicker: FC<DateRangePickerProps> = ({
  type: pickerType,
  value,
  onChange,
  open: controlledOpen,
  onOpenChange,
  placeholder = 'Pick a date range',
  children,
  onReset,
  minDate,
  maxDate,
  preservePresetSelectionWithinBounds = false,
  presetNames,
  resolvePresetDateRange = getPresetDateRange,
  showPresets = true,
  align,
  ariaLabel = 'Select date range',
  startDateAriaLabel = 'Start date',
  endDateAriaLabel = 'End date',
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const defaultRange = useMemo(() => createDefaultRange(), []);
  const availablePresetNames = useMemo(() => {
    const requestedPresetNames = presetNames ?? DEFAULT_PRESET_NAMES;

    if (preservePresetSelectionWithinBounds) {
      return [...requestedPresetNames];
    }

    const boundedPresetNames = getAllowedDateRangePickerPresetNames({
      allowedPresetNames: requestedPresetNames,
      minDate,
      maxDate,
      resolvePresetDateRange,
    });

    return requestedPresetNames.filter((name) => boundedPresetNames.includes(name));
  }, [maxDate, minDate, preservePresetSelectionWithinBounds, presetNames, resolvePresetDateRange]);
  const normalizedValue = useMemo(
    () =>
      clampDateRangePickerValueToBounds(value ?? defaultRange, {
        minDate,
        maxDate,
        allowedPresetNames: availablePresetNames,
        resolvePresetDateRange,
      }),
    [availablePresetNames, defaultRange, maxDate, minDate, resolvePresetDateRange, value],
  );
  const [tempValue, setTempValue] = useState<DateRangePickerDraftValue>(normalizedValue);
  const normalizedDateRange = useMemo(() => {
    if (!preservePresetSelectionWithinBounds || normalizedValue.type !== 'preset') {
      return getComputedDateRange(normalizedValue, resolvePresetDateRange);
    }

    return getComputedDateRange(
      clampDateRangePickerValueToBounds(normalizedValue, {
        minDate,
        maxDate,
        allowedPresetNames: [],
        resolvePresetDateRange,
      }),
      resolvePresetDateRange,
    );
  }, [
    maxDate,
    minDate,
    normalizedValue,
    preservePresetSelectionWithinBounds,
    resolvePresetDateRange,
  ]);
  const normalizedSelectionKey = `${normalizedValue.type === 'preset' ? normalizedValue.presetName : 'dateRange'}:${normalizedDateRange.from.valueOf()}:${normalizedDateRange.to.valueOf()}`;
  const previousNormalizedSelectionKeyRef = useRef(normalizedSelectionKey);
  const previousIsOpenRef = useRef(false);
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 960 : false,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 960);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const justOpened = isOpen && !previousIsOpenRef.current;
    previousIsOpenRef.current = isOpen;

    if (justOpened) {
      setTempValue(normalizedValue);
    }
  }, [isOpen, normalizedDateRange, normalizedValue]);

  useEffect(() => {
    if (previousNormalizedSelectionKeyRef.current === normalizedSelectionKey) {
      return;
    }

    previousNormalizedSelectionKeyRef.current = normalizedSelectionKey;
    setTempValue(normalizedValue);
  }, [normalizedDateRange, normalizedSelectionKey, normalizedValue]);

  const draftDateRange = useMemo<DateRangePickerDraft>(() => {
    if (tempValue.type === 'dateRange') {
      return tempValue.dateRange;
    }

    if (!preservePresetSelectionWithinBounds) {
      return resolvePresetDateRange(tempValue.presetName);
    }

    return getComputedDateRange(
      clampDateRangePickerValueToBounds(tempValue, {
        minDate,
        maxDate,
        allowedPresetNames: [],
        resolvePresetDateRange,
      }),
      resolvePresetDateRange,
    );
  }, [maxDate, minDate, preservePresetSelectionWithinBounds, resolvePresetDateRange, tempValue]);

  const setDateRange = (newRange: DateRangePickerDraft) => {
    if (newRange.to == null) {
      setTempValue({ type: 'dateRange', dateRange: newRange });
      return;
    }

    const nextValue = clampDateRangePickerValueToBounds(
      {
        type: 'dateRange',
        dateRange: { from: newRange.from, to: newRange.to },
      },
      {
        minDate,
        maxDate,
        allowedPresetNames: availablePresetNames,
        resolvePresetDateRange,
      },
    );

    setTempValue(nextValue);
  };

  const setPreset = (preset: PresetName) => {
    if (!availablePresetNames.includes(preset)) {
      return;
    }

    setTempValue({ type: 'preset', presetName: preset });
  };

  const handleStartDateChange = (date: DateRange['from']) => {
    const nextStartDate = clampDayjsToDateRangeBounds(date, {
      minDate,
      maxDate,
    });

    if (draftDateRange.to == null) {
      setDateRange({ from: nextStartDate });
      return nextStartDate;
    }

    const toDate = nextStartDate.isAfter(draftDateRange.to) ? nextStartDate : draftDateRange.to;

    setDateRange({ from: nextStartDate, to: toDate });
    return nextStartDate;
  };

  const handleEndDateChange = (date: DateRange['to']) => {
    const nextEndDate = clampDayjsToDateRangeBounds(date, { minDate, maxDate });
    const fromDate = nextEndDate.isBefore(draftDateRange.from) ? nextEndDate : draftDateRange.from;

    setDateRange({ from: fromDate, to: nextEndDate });
    return nextEndDate;
  };

  const handleCalendarSelect = (
    _selectedRange: CalendarDateRange | undefined,
    triggerDate: Date,
  ) => {
    const selectedDate = clampDayjsToDateRangeBounds(dayjs(triggerDate), {
      minDate,
      maxDate,
    });

    if (draftDateRange.to != null || selectedDate.isBefore(draftDateRange.from, 'day')) {
      setDateRange({ from: selectedDate });
      return;
    }

    setDateRange({ from: draftDateRange.from, to: selectedDate });
  };

  const handleCancel = () => {
    setTempValue(normalizedValue);
    setIsOpen(false);
  };

  const handleUpdate = () => {
    if (draftDateRange.to == null) {
      return;
    }

    const nextValue = clampDateRangePickerValueToBounds(
      tempValue.type === 'preset'
        ? tempValue
        : {
            type: 'dateRange',
            dateRange: {
              from: tempValue.dateRange.from,
              to: draftDateRange.to,
            },
          },
      {
        minDate,
        maxDate,
        allowedPresetNames: availablePresetNames,
        resolvePresetDateRange,
      },
    );

    onChange?.(nextValue);
    setIsOpen(false);
  };

  const buttonDisplayText = useMemo(
    () => getDisplayText(value ? normalizedValue : undefined, placeholder),
    [normalizedValue, placeholder, value],
  );

  const isFilterType = pickerType === 'filter' || pickerType === 'filter-resettable';
  const showResetButton = pickerType === 'filter-resettable' && Boolean(value);
  const triggerElement = (() => {
    if (pickerType === 'custom') {
      return children;
    }

    if (isFilterType) {
      return (
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'outline', size: 'default' }),
            'shadow-none',
            showResetButton && '!pr-9',
          )}
        >
          <CalendarIcon className="size-4" />
          {buttonDisplayText}
        </button>
      );
    }

    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 rounded-full px-3 shadow-none"
      >
        <CalendarIcon className="size-3.5" />
        <span>{buttonDisplayText}</span>
      </Button>
    );
  })();

  return (
    <div
      ref={rootRef}
      className={cn('flex items-center gap-3', isFilterType && 'relative inline-flex')}
    >
      <Popover open={isOpen} onOpenChange={setIsOpen} modal>
        <PopoverTrigger asChild>{triggerElement}</PopoverTrigger>
        <PopoverContent
          aria-label={ariaLabel}
          align={align ?? (isFilterType ? 'end' : 'start')}
          side="bottom"
          sideOffset={8}
          className="w-max max-w-[calc(100vw-1rem)] overflow-y-auto p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <span>
            <DateRangePickerPanel
              draftDateRange={draftDateRange}
              isSmallScreen={isSmallScreen}
              minDate={minDate}
              maxDate={maxDate}
              startDateAriaLabel={startDateAriaLabel}
              endDateAriaLabel={endDateAriaLabel}
              onCalendarSelect={handleCalendarSelect}
              onCancel={handleCancel}
              onEndDateChange={handleEndDateChange}
              onPresetSelect={setPreset}
              onStartDateChange={handleStartDateChange}
              onUpdate={handleUpdate}
              presetNames={availablePresetNames}
              selectedPresetName={
                draftDateRange.to != null && tempValue.type === 'preset'
                  ? tempValue.presetName
                  : undefined
              }
              showPresets={showPresets}
            />
          </span>
        </PopoverContent>
      </Popover>
      {showResetButton ? (
        <button
          type="button"
          aria-label="Reset date range"
          className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring absolute top-1/2 right-1 z-10 flex size-7 -translate-y-1/2 items-center justify-center rounded-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onReset();
          }}
        >
          <XIcon className="size-4" />
        </button>
      ) : null}
    </div>
  );
};

function PresetButton({
  label,
  isSelected,
  onClick,
}: {
  label: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      aria-pressed={isSelected}
      type="button"
      variant={isSelected ? 'secondary' : 'ghost'}
      size="lg"
      className={cn('justify-start', isSelected && 'pointer-events-none')}
      onClick={onClick}
    >
      <CheckIcon
        className={cn('size-4 opacity-0 transition-opacity', isSelected && 'opacity-70')}
      />
      <span>{label}</span>
    </Button>
  );
}

export { DATE_FORMAT, DateRangePicker };
